import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Calendar, Edit2, Trash2, CheckSquare, ChevronDown } from 'lucide-react';
import { SOLICITACAO_TIPOS, SOLICITACAO_TIPO_GRUPOS, tiposDoGrupo } from './solicitacaoTipos';

interface FilterState {
    status: string[];
    titulo: string;
    prioridade: string;
    responsavelId: string[];
    /** Quem abriu o chamado (tarefas.solicitante_id) — independente de quem executa. */
    solicitanteId: string[];
    vencimentoInicio: string;
    vencimentoFim: string;
    tipoSolicitacao: string[];
    projetoId: string[];
}

interface TasksFilterModalProps {
    isOpen: boolean;
    onClose: () => void;
    onApply: (filters: FilterState) => void;
    currentFilters: FilterState;
    users: { id: string, full_name: string, status?: string }[];
    /** Só na fila de Solicitações — em Tarefas o campo é sempre null. */
    showTipoSolicitacao?: boolean;
    projetos?: { id: number | string; nome: string; status?: string }[];
}

interface PessoaMultiSelectProps {
    label: string;
    /** Texto do estado "nenhum selecionado", ex.: "Todos os responsáveis". */
    labelTodos: string;
    /** Sufixo da contagem, ex.: "responsável(is) selecionado(s)". */
    labelContagem: string;
    users: { id: string, full_name: string, status?: string }[];
    selected: string[];
    onChange: (ids: string[]) => void;
    aberto: boolean;
    onToggle: () => void;
}

/** Seletor múltiplo de pessoas. Responsáveis e "aberto por" usam o mesmo, só muda o rótulo. */
const PessoaMultiSelect: React.FC<PessoaMultiSelectProps> = ({
    label, labelTodos, labelContagem, users, selected, onChange, aberto, onToggle,
}) => (
    <div className="space-y-1.5 col-span-1 md:col-span-2">
        <label className="text-sm font-bold text-gray-700 dark:text-gray-300 flex items-center justify-between">
            {label}
            <button
                onClick={() => onChange([])}
                className="text-xs text-primary hover:underline font-medium"
            >
                Limpar
            </button>
        </label>
        <div className="relative">
            <button
                type="button"
                onClick={onToggle}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none text-gray-900 dark:text-white transition-all text-left"
            >
                <span className="truncate flex-1">
                    {selected.length === 0 ? labelTodos : `${selected.length} ${labelContagem}`}
                </span>
                <ChevronDown size={16} className={`text-gray-400 transition-transform flex-shrink-0 ${aberto ? 'rotate-180' : ''}`} />
            </button>

            {aberto && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-xl z-50 overflow-hidden py-1">
                    <label className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700">
                        <input
                            type="checkbox"
                            checked={selected.length === 0}
                            onChange={() => onChange([])}
                            className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary accent-primary"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{labelTodos}</span>
                    </label>
                    <div className="max-h-48 overflow-y-auto custom-scrollbar pt-1">
                        {users.filter(u => u.status !== 'inactive').map(u => {
                            const isSelected = selected.includes(u.id);
                            return (
                                <label key={u.id} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={(e) => onChange(e.target.checked
                                            ? [...selected, u.id]
                                            : selected.filter(id => id !== u.id))}
                                        className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary accent-primary"
                                    />
                                    <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                                        {u.full_name?.charAt(0)?.toUpperCase() || '?'}
                                    </div>
                                    <span className={`text-sm truncate ${isSelected ? 'font-bold text-primary dark:text-primary' : 'font-medium text-gray-600 dark:text-gray-300'}`}>
                                        {u.full_name}
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    </div>
);

const TasksFilterModal: React.FC<TasksFilterModalProps> = ({ isOpen, onClose, onApply, currentFilters, users, showTipoSolicitacao = false, projetos = [] }) => {
    // Defensive initialization
    const [filters, setFilters] = useState<FilterState>(currentFilters || {
        status: [], titulo: '', prioridade: 'all', responsavelId: [], solicitanteId: [],
        vencimentoInicio: '', vencimentoFim: '', tipoSolicitacao: [], projetoId: []
    });

    const statusOptions = [
        { value: 'a_iniciar', label: 'A Iniciar' },
        { value: 'em_andamento', label: 'Em Andamento' },
        { value: 'aguardando', label: 'Aguardando' },
        { value: 'concluido', label: 'Concluído' },
        { value: 'cancelado', label: 'Cancelado' }
    ];

    const priorityOptions = [
        { value: 'urgencia', label: 'Urgência', color: 'text-rose-600' },
        { value: 'alta', label: 'Alta', color: 'text-red-500' },
        { value: 'media', label: 'Média', color: 'text-yellow-500' },
        { value: 'baixa', label: 'Baixa', color: 'text-green-500' }
    ];

    const [openDropdown, setOpenDropdown] = useState<'status' | 'responsavel' | 'solicitante' | 'tipo' | 'projeto' | null>(null);
    const modalRef = useRef<HTMLDivElement>(null);

    // Update state when prop changes
    useEffect(() => {
        if (currentFilters) {
            // tipoSolicitacao e solicitanteId podem faltar em filtros salvos no localStorage
            // antes dessas versões — sem o fallback o .length/.includes abaixo quebra.
            setFilters({
                ...currentFilters,
                tipoSolicitacao: currentFilters.tipoSolicitacao ?? [],
                solicitanteId: currentFilters.solicitanteId ?? [],
                projetoId: currentFilters.projetoId ?? [],
            });
        }
    }, [currentFilters, isOpen]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
            setOpenDropdown(null);
        }
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
                setOpenDropdown(null);
            }
        };

        if (openDropdown !== null) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openDropdown]);

    const handleClear = () => {
        const emptyState: FilterState = {
            status: [],
            titulo: '',
            prioridade: 'all',
            responsavelId: [],
            solicitanteId: [],
            vencimentoInicio: '',
            vencimentoFim: '',
            tipoSolicitacao: [],
            projetoId: []
        };
        setFilters(emptyState);
    };

    const handleApply = () => {
        onApply(filters);
        onClose();
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto p-4 md:p-10">
            {/* Backdrop with animation and blur */}
            <div
                className="fixed inset-0 bg-black/60 backdrop-blur-[6px] animate-in fade-in duration-300"
                onClick={onClose}
            />

            {/* Modal Container */}
            <div className="relative bg-white dark:bg-gray-800 w-full max-w-2xl shadow-2xl flex flex-col rounded-2xl animate-in zoom-in-95 slide-in-from-top-4 duration-300 border border-gray-100 dark:border-gray-700 mt-4 md:mt-0" ref={modalRef}>
                {/* Header */}
                <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-white dark:bg-gray-800 rounded-t-2xl">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                            <Calendar size={20} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Filtros de Tarefas</h2>
                            <p className="text-xs text-gray-400 dark:text-gray-500 font-medium">Personalize sua visualização</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="bg-primary/20 text-primary px-2.5 py-1 rounded-lg text-xs font-bold">
                            {filters && Object.entries(filters).filter(([k, v]) => Array.isArray(v) ? v.length > 0 : (v && v !== 'all')).length} ativos
                        </span>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all border border-transparent hover:border-gray-100 dark:hover:border-gray-600">
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Body - Scrollable */}
                <div className="flex-1 p-6 space-y-6 custom-scrollbar bg-white dark:bg-gray-800">

                    {/* Columns Layout */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                        {/* Titulo */}
                        <div className="space-y-1.5 col-span-1 md:col-span-2">
                            <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Título da Tarefa</label>
                            <input
                                type="text"
                                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none text-gray-900 dark:text-white transition-all placeholder:text-gray-400"
                                placeholder="Buscar por título..."
                                value={filters.titulo}
                                onChange={(e) => setFilters({ ...filters, titulo: e.target.value })}
                            />
                        </div>

                        {/* Status */}
                        <div className="space-y-1.5 col-span-1 md:col-span-2">
                            <label className="text-sm font-bold text-gray-700 dark:text-gray-300 flex items-center justify-between">
                                Status
                                <button
                                    onClick={() => setFilters({ ...filters, status: [] })}
                                    className="text-xs text-primary hover:underline font-medium"
                                >
                                    Limpar
                                </button>
                            </label>
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={() => setOpenDropdown(openDropdown === 'status' ? null : 'status')}
                                    className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none text-gray-900 dark:text-white transition-all text-left"
                                >
                                    <span className="truncate flex-1">
                                        {filters.status.length === 0
                                            ? 'Todos os status'
                                            : `${filters.status.length} status selecionado${filters.status.length > 1 ? 's' : ''}`}
                                    </span>
                                    <ChevronDown size={16} className={`text-gray-400 transition-transform flex-shrink-0 ${openDropdown === 'status' ? 'rotate-180' : ''}`} />
                                </button>

                                {openDropdown === 'status' && (
                                    <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-xl z-50 overflow-hidden py-1">
                                        <label className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700">
                                            <input
                                                type="checkbox"
                                                checked={filters.status.length === 0}
                                                onChange={() => setFilters({ ...filters, status: [] })}
                                                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary accent-primary"
                                            />
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Todos os status</span>
                                        </label>
                                        <div className="max-h-48 overflow-y-auto custom-scrollbar pt-1">
                                            {statusOptions.map(opt => {
                                                const isSelected = filters.status.includes(opt.value);
                                                return (
                                                    <label key={opt.value} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={(e) => {
                                                                const newStatus = e.target.checked
                                                                    ? [...filters.status, opt.value]
                                                                    : filters.status.filter(s => s !== opt.value);
                                                                setFilters({ ...filters, status: newStatus });
                                                            }}
                                                            className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary accent-primary"
                                                        />
                                                        <span className={`text-sm ${isSelected ? 'font-bold text-primary dark:text-primary' : 'font-medium text-gray-600 dark:text-gray-300'}`}>
                                                            {opt.label}
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Tipo de Solicitação — só na fila de Solicitações */}
                        {showTipoSolicitacao && (
                            <div className="space-y-1.5 col-span-1 md:col-span-2">
                                <label className="text-sm font-bold text-gray-700 dark:text-gray-300 flex items-center justify-between">
                                    Tipo de Solicitação
                                    <button
                                        onClick={() => setFilters({ ...filters, tipoSolicitacao: [] })}
                                        className="text-xs text-primary hover:underline font-medium"
                                    >
                                        Limpar
                                    </button>
                                </label>
                                <div className="relative">
                                    <button
                                        type="button"
                                        onClick={() => setOpenDropdown(openDropdown === 'tipo' ? null : 'tipo')}
                                        className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none text-gray-900 dark:text-white transition-all text-left"
                                    >
                                        <span className="truncate flex-1">
                                            {filters.tipoSolicitacao.length === 0
                                                ? 'Todos os tipos'
                                                : filters.tipoSolicitacao.length === 1
                                                    ? SOLICITACAO_TIPOS.find(t => t.value === filters.tipoSolicitacao[0])?.label || '1 tipo selecionado'
                                                    : `${filters.tipoSolicitacao.length} tipos selecionados`}
                                        </span>
                                        <ChevronDown size={16} className={`text-gray-400 transition-transform flex-shrink-0 ${openDropdown === 'tipo' ? 'rotate-180' : ''}`} />
                                    </button>

                                    {openDropdown === 'tipo' && (
                                        <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-xl z-50 overflow-hidden py-1">
                                            <label className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700">
                                                <input
                                                    type="checkbox"
                                                    checked={filters.tipoSolicitacao.length === 0}
                                                    onChange={() => setFilters({ ...filters, tipoSolicitacao: [] })}
                                                    className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary accent-primary"
                                                />
                                                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Todos os tipos</span>
                                            </label>
                                            <div className="max-h-64 overflow-y-auto custom-scrollbar pt-1">
                                                {SOLICITACAO_TIPO_GRUPOS.map(grupo => (
                                                    <div key={grupo}>
                                                        <p className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">{grupo}</p>
                                                        {tiposDoGrupo(grupo).map(opt => {
                                                            const isSelected = filters.tipoSolicitacao.includes(opt.value);
                                                            return (
                                                                <label key={opt.value} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isSelected}
                                                                        onChange={(e) => {
                                                                            const newTipos = e.target.checked
                                                                                ? [...filters.tipoSolicitacao, opt.value]
                                                                                : filters.tipoSolicitacao.filter(t => t !== opt.value);
                                                                            setFilters({ ...filters, tipoSolicitacao: newTipos });
                                                                        }}
                                                                        className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary accent-primary"
                                                                    />
                                                                    <span className={`text-sm ${isSelected ? 'font-bold text-primary dark:text-primary' : 'font-medium text-gray-600 dark:text-gray-300'}`}>
                                                                        {opt.label}
                                                                    </span>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Prioridade */}
                        <div className="space-y-1.5">
                            <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Prioridade</label>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={() => setFilters({ ...filters, prioridade: 'all' })}
                                    className={`px-4 py-2 rounded-xl text-xs font-bold border transition-all ${filters.prioridade === 'all'
                                        ? 'bg-primary text-white border-primary shadow-lg shadow-primary/20'
                                        : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                                        }`}
                                >
                                    Todas
                                </button>
                                {priorityOptions.map(opt => (
                                    <button
                                        key={opt.value}
                                        onClick={() => setFilters({ ...filters, prioridade: opt.value })}
                                        className={`px-4 py-2 rounded-xl text-xs font-bold border transition-all ${filters.prioridade === opt.value
                                            ? `bg-opacity-10 dark:bg-opacity-20 border-opacity-50 ${opt.color.replace('text-', 'bg-').replace('500', '500')} ${opt.color} border-${opt.color.replace('text-', '').replace('500', '500')} ring-2 ring-${opt.color.replace('text-', '').replace('500', '500')}/20`
                                            : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                                            }`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Responsável — quem executa */}
                        <PessoaMultiSelect
                            label="Responsáveis"
                            labelTodos="Todos os responsáveis"
                            labelContagem="responsável(is) selecionado(s)"
                            users={users}
                            selected={filters.responsavelId}
                            onChange={(ids) => setFilters({ ...filters, responsavelId: ids })}
                            aberto={openDropdown === 'responsavel'}
                            onToggle={() => setOpenDropdown(openDropdown === 'responsavel' ? null : 'responsavel')}
                        />

                        {/* Solicitante — quem abriu o chamado, que nem sempre é quem executa */}
                        <PessoaMultiSelect
                            label="Aberto por"
                            labelTodos="Qualquer pessoa"
                            labelContagem="pessoa(s) selecionada(s)"
                            users={users}
                            selected={filters.solicitanteId}
                            onChange={(ids) => setFilters({ ...filters, solicitanteId: ids })}
                            aberto={openDropdown === 'solicitante'}
                            onToggle={() => setOpenDropdown(openDropdown === 'solicitante' ? null : 'solicitante')}
                        />

                        {/* Projeto — só na fila de Solicitações */}
                        {showTipoSolicitacao && (
                            <PessoaMultiSelect
                                label="Projeto"
                                labelTodos="Qualquer projeto"
                                labelContagem="projeto(s) selecionado(s)"
                                users={projetos.filter(p => p.status !== 'inactive' && p.status !== 'concluido').map(p => ({ id: String(p.id), full_name: p.nome, status: 'active' }))}
                                selected={filters.projetoId}
                                onChange={(ids) => setFilters({ ...filters, projetoId: ids })}
                                aberto={openDropdown === 'projeto'}
                                onToggle={() => setOpenDropdown(openDropdown === 'projeto' ? null : 'projeto')}
                            />
                        )}

                        {/* Data Vencimento (Range) */}
                        <div className="space-y-1.5 col-span-1 md:col-span-2">
                            <label className="text-sm font-bold text-gray-700 dark:text-gray-300">Data de Vencimento (Período)</label>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="relative group">
                                    <span className="absolute left-4 top-2 text-[10px] uppercase font-bold text-gray-400 group-focus-within:text-primary transition-colors">Início</span>
                                    <input
                                        type="date"
                                        className="w-full pl-4 pt-6 pb-2 pr-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none text-gray-600 dark:text-gray-300 transition-all"
                                        value={filters.vencimentoInicio}
                                        onChange={(e) => setFilters({ ...filters, vencimentoInicio: e.target.value })}
                                    />
                                </div>
                                <div className="relative group">
                                    <span className="absolute left-4 top-2 text-[10px] uppercase font-bold text-gray-400 group-focus-within:text-primary transition-colors">Fim</span>
                                    <input
                                        type="date"
                                        className="w-full pl-4 pt-6 pb-2 pr-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none text-gray-600 dark:text-gray-300 transition-all"
                                        value={filters.vencimentoFim}
                                        onChange={(e) => setFilters({ ...filters, vencimentoFim: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>

                    </div>
                    {/* Add extra padding at bottom */}
                    <div className="h-4"></div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-100 dark:border-gray-700 flex gap-4 bg-gray-50/30 dark:bg-gray-900/30 rounded-b-2xl">
                    <button
                        onClick={handleClear}
                        className="flex-1 py-3 px-4 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-600 dark:text-gray-300 font-bold text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-all hover:border-gray-300 dark:hover:border-gray-500"
                    >
                        Limpar filtros
                    </button>
                    <button
                        onClick={handleApply}
                        className="flex-1 py-3 px-4 bg-primary text-white rounded-xl font-bold text-sm hover:bg-primary/90 transition-all shadow-lg shadow-primary/25 active:scale-95"
                    >
                        Aplicar filtros
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default TasksFilterModal;
